import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, TrendingUp } from 'lucide-react';
import { Modal } from './Tables';

const CATEGORIES = ['snacks','drinks','hot drinks','food','other'];
const EMPTY = { name:'', price_type:'fixed', buying_price:'', selling_price:'', category:'snacks', is_active:1 };

export default function Food() {
  const [items,    setItems]    = useState([]);
  const [modal,    setModal]    = useState(null);
  const [selected, setSelected] = useState(null);
  const [form,     setForm]     = useState(EMPTY);

  const load = useCallback(async () => {
    const res = await api.get('/food?type=menu');
    setItems(Array.isArray(res.data) ? res.data : []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(EMPTY); setModal('add'); };
  const openEdit = (item) => { setSelected(item); setForm({...item, buying_price:item.buying_price||'', selling_price:item.selling_price||''}); setModal('edit'); };

  const save = async () => {
    if (!form.name.trim())        return toast.error('Item name required');
    if (!form.selling_price)      return toast.error('Selling price required');
    try {
      if (modal==='add') { await api.post('/food?type=menu', form); toast.success('Item added'); }
      else { await api.put('/food?type=menu&id='+selected.id, form); toast.success('Updated'); }
      setModal(null); load();
    } catch(e) { toast.error(e.response?.data?.error||'Error'); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove "${item.name}"?`)) return;
    try { await api.delete('/food?type=menu&id='+item.id); toast.success('Removed'); load(); }
    catch(e) { toast.error(e.response?.data?.error||'Error'); }
  };

  // Profit summary
  const totalProfit = items.reduce((a,i) => a + (parseFloat(i.selling_price||0) - parseFloat(i.buying_price||0)), 0);

  const grouped = CATEGORIES.reduce((acc,cat) => {
    const f = items.filter(i=>i.category===cat);
    if (f.length) acc[cat]=f;
    return acc;
  }, {});

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Menu Management</h2>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={16}/> Add Item</button>
      </div>

      {/* Profit summary */}
      <div className="inv-stats-row" style={{ marginBottom:20 }}>
        <div className="inv-stat"><div className="inv-stat-value">{items.length}</div><div className="inv-stat-label">Menu Items</div></div>
        <div className="inv-stat">
          <div className="inv-stat-value" style={{ color:'#22c55e' }}>
            ₹{items.reduce((a,i)=>a+parseFloat(i.selling_price||0),0).toFixed(0)}
          </div>
          <div className="inv-stat-label">Avg Selling/item</div>
        </div>
        <div className="inv-stat">
          <div className="inv-stat-value" style={{ color:'#2563eb' }}>
            ₹{items.reduce((a,i)=>a+parseFloat(i.buying_price||0),0).toFixed(0)}
          </div>
          <div className="inv-stat-label">Avg Cost/item</div>
        </div>
      </div>

      {Object.entries(grouped).map(([cat,catItems]) => (
        <div className="card" key={cat} style={{ marginBottom:20 }}>
          <div className="card-header">
            <span style={{ textTransform:'capitalize', fontWeight:600 }}>{cat}</span>
          </div>
          <table className="data-table">
            <thead>
              <tr><th>Name</th><th>Type</th><th>Buying (₹)</th><th>Selling (₹)</th><th>Margin</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {catItems.map(item => {
                const margin = parseFloat(item.selling_price||0) - parseFloat(item.buying_price||0);
                const pct    = item.buying_price > 0 ? Math.round((margin/parseFloat(item.buying_price))*100) : 0;
                return (
                  <tr key={item.id}>
                    <td><strong>{item.name}</strong></td>
                    <td><span className={`badge badge-${item.price_type==='fixed'?'available':'paused'}`}>{item.price_type}</span></td>
                    <td>₹{Number(item.buying_price||0).toFixed(2)}</td>
                    <td>₹{Number(item.selling_price||0).toFixed(2)}</td>
                    <td>
                      <span style={{ color: margin>=0?'#22c55e':'#ef4444', fontWeight:600, fontSize:13 }}>
                        ₹{margin.toFixed(2)} {item.buying_price>0&&<span style={{ color:'#94a3b8', fontSize:11 }}>({pct}%)</span>}
                      </span>
                    </td>
                    <td><span className={`badge badge-${item.is_active?'active':'ended'}`}>{item.is_active?'Active':'Off'}</span></td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-outline btn-sm" onClick={()=>openEdit(item)}><Pencil size={13}/></button>
                        <button className="btn btn-danger btn-sm" onClick={()=>remove(item)}><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
      {items.length===0&&<div className="empty-state">No menu items yet</div>}

      {(modal==='add'||modal==='edit') && (
        <Modal title={modal==='add'?'Add Menu Item':'Edit Menu Item'} onClose={()=>setModal(null)}>
          <div className="form-group">
            <label>Item Name</label>
            <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Cold Drink"/>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Price Type</label>
              <select value={form.price_type} onChange={e=>setForm(f=>({...f,price_type:e.target.value}))}>
                <option value="fixed">Fixed</option>
                <option value="variable">Variable</option>
              </select>
            </div>
            <div className="form-group">
              <label>Category</label>
              <select value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                {CATEGORIES.map(c=><option key={c} value={c} style={{textTransform:'capitalize'}}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Buying Price (₹) — your cost</label>
              <input type="number" step="0.01" value={form.buying_price}
                onChange={e=>setForm(f=>({...f,buying_price:e.target.value}))} placeholder="0.00"/>
            </div>
            <div className="form-group">
              <label>Selling Price (₹) — charged to customer</label>
              <input type="number" step="0.01" value={form.selling_price}
                onChange={e=>setForm(f=>({...f,selling_price:e.target.value}))} placeholder="0.00"/>
            </div>
          </div>
          {form.buying_price&&form.selling_price&&(
            <div style={{ background: parseFloat(form.selling_price)>=parseFloat(form.buying_price)?'#f0fdf4':'#fef2f2', border:'1px solid', borderColor: parseFloat(form.selling_price)>=parseFloat(form.buying_price)?'#bbf7d0':'#fca5a5', borderRadius:8, padding:'8px 14px', fontSize:13, color: parseFloat(form.selling_price)>=parseFloat(form.buying_price)?'#15803d':'#b91c1c' }}>
              Margin: ₹{(parseFloat(form.selling_price||0)-parseFloat(form.buying_price||0)).toFixed(2)} per item
            </div>
          )}
          <div className="form-row" style={{ marginTop:12 }}>
            <div className="form-group">
              <label>Status</label>
              <select value={form.is_active} onChange={e=>setForm(f=>({...f,is_active:parseInt(e.target.value)}))}>
                <option value={1}>Active</option><option value={0}>Inactive</option>
              </select>
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={()=>setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={save}>Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
